import type { FC } from 'react';
import { useCallback } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type { SavedPrompt } from 'src/components/SupportCase/types';
import { PANEL_TOOLTIP_SLOT_PROPS } from 'src/components/SupportCase/panelTooltip';

interface SavedPromptChipsProps {
  prompts: SavedPrompt[];
  /** Run one. The caller sends it as the first message, exactly as if typed. */
  onRun: (prompt: SavedPrompt) => void;
  /** Hidden while the agent is working or the case is closed. */
  disabled?: boolean;
}

/**
 * The saved prompts, under the composer's action row (ENG-4220).
 *
 * Deliberately below the field and not above it: the chip is a shortcut for *writing*
 * a message, so it sits with the send button rather than pushing the conversation
 * down. The pattern is `SentAttachmentChips` — a wrapping row of small outlined chips,
 * a tooltip carrying the full text (a title is 60 characters; the question is not),
 * and nothing at all when there are none.
 *
 * Renders `null` when empty rather than an empty row, so a new user sees no orphan
 * whitespace under the composer.
 */
const SavedPromptChips: FC<SavedPromptChipsProps> = ({
  prompts,
  onRun,
  disabled = false,
}) => {
  const handleClick = useCallback(
    (prompt: SavedPrompt) => {
      onRun(prompt);
    },
    [onRun]
  );

  if (prompts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ pt: 0.5 }} data-testid="saved-prompt-chips">
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {prompts.map((prompt) => (
          <Tooltip
            key={prompt.id}
            slotProps={PANEL_TOOLTIP_SLOT_PROPS}
            title={prompt.body}
          >
            <span>
              <Chip
                size="small"
                variant="outlined"
                label={prompt.title}
                onClick={() => handleClick(prompt)}
                disabled={disabled}
                data-testid={`saved-prompt-${prompt.id}`}
              />
            </span>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
};

export default SavedPromptChips;
